import type {
  BudgetConfig,
  BudgetState,
  BudgetPolicy,
  FinOpsEvent,
  CostAlertPayload,
  CostUpdatePayload,
} from './types';
import { BudgetConfigSchema } from './types';

/**
 * Budget manager for FinOps guardrails.
 * Tracks token usage and enforces spending limits.
 * 
 * @example
 * ```typescript
 * const manager = createBudgetManager({
 *   usd: 10,
 *   perRequestCents: 50,
 *   onExceed: 'halt',
 * });
 * 
 * const state = manager.recordUsage(1000, 0.002);
 * console.log(`Total spend: $${state.totalUsd.toFixed(4)}`);
 * ```
 */
export class BudgetManager {
  private state: BudgetState;
  private config: { usd: number; perRequestCents: number; onExceed: BudgetPolicy };
  private eventHandlers: Array<(event: FinOpsEvent) => void> = [];

  constructor(config: BudgetConfig = {}) {
    // Validate configuration
    const validated = BudgetConfigSchema.parse(config);

    // Set defaults
    const usd = validated.usd !== undefined ? validated.usd : Infinity;
    const perRequestCents = validated.perRequestCents !== undefined ? validated.perRequestCents : Infinity;
    const onExceed = validated.onExceed !== undefined ? validated.onExceed : ('halt' as BudgetPolicy);

    this.config = { usd, perRequestCents, onExceed };

    this.state = {
      totalUsd: 0,
      requestCount: 0,
      lastRequestCents: 0,
      exceeded: false,
      remainingUsd: usd,
    };
  }

  /**
   * Record a request's token usage and cost.
   * 
   * @param tokens - Number of tokens used
   * @param costPerMTokenUsd - Cost per million tokens in USD
   * @param model - Model name (optional, for logging)
   * @returns Updated budget state
   * 
   * @example
   * ```typescript
   * const state = manager.recordUsage(1000, 0.002, 'gpt-4o-mini');
   * if (state.exceeded) {
   *   console.warn('Budget exceeded!');
   * }
   * ```
   */
  recordUsage(tokens: number, costPerMTokenUsd: number, model?: string): BudgetState {
    const requestCostUsd = (tokens / 1_000_000) * costPerMTokenUsd;
    const requestCostCents = requestCostUsd * 100;

    this.state.lastRequestCents = requestCostCents;
    this.state.totalUsd += requestCostUsd;
    this.state.requestCount += 1;
    this.state.remainingUsd = Math.max(0, this.config.usd - this.state.totalUsd);

    // Check limits
    const sessionExceeded = this.state.totalUsd > this.config.usd;
    const requestExceeded = requestCostCents > this.config.perRequestCents;

    if (sessionExceeded || requestExceeded) {
      this.state.exceeded = true;
      const alertPayload: CostAlertPayload = {
        totalUsd: this.state.totalUsd,
        limitUsd: this.config.usd,
        requestCents: requestCostCents,
        requestLimitCents: this.config.perRequestCents,
        policy: this.config.onExceed,
        timestamp: Date.now(),
      };
      if (model) {
        alertPayload.model = model;
      }
      this.emitCostAlert(alertPayload);
    }

    const updatePayload: CostUpdatePayload = {
      totalUsd: this.state.totalUsd,
      requestCents: requestCostCents,
      requestCount: this.state.requestCount,
      exceeded: this.state.exceeded,
      timestamp: Date.now(),
    };
    if (model) {
      updatePayload.model = model;
    }
    this.emitCostUpdate(updatePayload);

    return { ...this.state };
  }

  /**
   * Check if a request would exceed the budget.
   * 
   * @param estimatedTokens - Estimated token count for the request
   * @param costPerMTokenUsd - Cost per million tokens in USD
   * @returns True if the request would exceed budget
   * 
   * @example
   * ```typescript
   * if (manager.wouldExceed(5000, 0.002)) {
   *   console.warn('Request would exceed budget');
   * }
   * ```
   */
  wouldExceed(estimatedTokens: number, costPerMTokenUsd: number): boolean {
    const estimatedCostUsd = (estimatedTokens / 1_000_000) * costPerMTokenUsd;
    const estimatedCostCents = estimatedCostUsd * 100;

    const wouldExceedSession = this.state.totalUsd + estimatedCostUsd > this.config.usd;
    const wouldExceedRequest = estimatedCostCents > this.config.perRequestCents;

    return wouldExceedSession || wouldExceedRequest;
  }

  /**
   * Get the current budget state.
   * 
   * @returns Current budget state (immutable copy)
   * 
   * @example
   * ```typescript
   * const state = manager.getState();
   * console.log(`Spent: $${state.totalUsd.toFixed(4)}`);
   * console.log(`Remaining: $${state.remainingUsd.toFixed(4)}`);
   * ```
   */
  getState(): BudgetState {
    return { ...this.state };
  }

  /**
   * Reset the budget state.
   * 
   * @example
   * ```typescript
   * manager.reset();
   * console.log(manager.getState().totalUsd); // 0
   * ```
   */
  reset(): void {
    this.state = {
      totalUsd: 0,
      requestCount: 0,
      lastRequestCents: 0,
      exceeded: false,
      remainingUsd: this.config.usd,
    };
    this.emit({
      type: 'budget:reset',
      timestamp: Date.now(),
      payload: { remainingUsd: this.state.remainingUsd },
    });
  }

  /**
   * Get the budget policy.
   * 
   * @returns Current budget policy
   */
  getPolicy(): BudgetPolicy | undefined {
    return this.config.onExceed;
  }

  /**
   * Get the budget configuration.
   * 
   * @returns Current budget configuration
   */
  getConfig(): { usd: number; perRequestCents: number; onExceed: BudgetPolicy } {
    return { ...this.config };
  }

  /**
   * Attach an event handler for budget events.
   * 
   * @param handler - Event handler function
   * 
   * @example
   * ```typescript
   * manager.onEvent((event) => {
   *   if (event.type === 'cost:alert') {
   *     console.error('Cost alert:', event.payload);
   *   }
   * });
   * ```
   */
  onEvent(handler: (event: FinOpsEvent) => void): void {
    this.eventHandlers.push(handler);
  }

  private emit(event: FinOpsEvent): void {
    this.eventHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.error('[BudgetManager] Event handler error:', error);
      }
    });
  }

  private emitCostAlert(payload: CostAlertPayload): void {
    this.emit({
      type: 'cost:alert',
      timestamp: Date.now(),
      payload,
    });
  }

  private emitCostUpdate(payload: CostUpdatePayload): void {
    this.emit({
      type: 'cost:update',
      timestamp: Date.now(),
      payload,
    });
  }
}

/**
 * Create a budget manager with the given configuration.
 * 
 * @param config - Budget configuration
 * @returns Budget manager instance
 * 
 * @example
 * ```typescript
 * const manager = createBudgetManager({
 *   usd: 5,
 *   perRequestCents: 20,
 *   onExceed: 'halt',
 * });
 * 
 * manager.recordUsage(1000, 0.002);
 * console.log(manager.getState());
 * ```
 */
export function createBudgetManager(config: BudgetConfig = {}): BudgetManager {
  return new BudgetManager(config);
}

/**
 * Estimate token count for a text string (rough approximation).
 * Uses ~4 characters per token heuristic (GPT models).
 * 
 * @param text - Input text
 * @returns Estimated token count
 * 
 * @example
 * ```typescript
 * const tokens = estimateTokens('Hello, world!');
 * console.log(`Estimated tokens: ${tokens}`); // ~4
 * ```
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

/**
 * Calculate total token usage from input and output.
 * 
 * @param input - Input token count
 * @param output - Output token count
 * @returns Token usage breakdown
 * 
 * @example
 * ```typescript
 * const usage = calculateTokens(100, 50);
 * console.log(`Total: ${usage.total}`); // 150
 * ```
 */
export function calculateTokens(input: number, output: number): {
  input: number;
  output: number;
  total: number;
} {
  return {
    input,
    output,
    total: input + output,
  };
}

